// apps/web/src/features/crm/leads/components/LeadClientMatchDialog.tsx
//
// CRM v4 M3 (#2244 FR-2/FR-3, #2247 FR-4). Semi-automatic lead->client matching
// dialog: ONE confirmation binds the lead to a client, either by matching an
// existing client contact (registry-identifier match) or by creating a new
// contact seeded from the lead's draft name. Reusable and self-contained — the
// future full-page 3-zone card mounts this same component + `useLeadBinding`
// hooks; nothing here is throwaway.
//
// WHY EditorModal (not raw Modal): the create-mode branch exposes an editable
// contact-name field, so the surface routes its close paths through the shared
// unsaved-changes guard (#680) instead of the raw `Modal`. `dirty` tracks
// whether the manager has diverged from the pre-filled proposal; the Cancel
// affordance lives inside the guard subtree so it prompts when dirty.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BindLeadInput } from '@magimp/shared';
import {
  Button,
  EditorModal,
  ErrorState,
  FormRow,
  Input,
  LoadingState,
  Select,
  useEditorGuard,
  type SelectOption,
} from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';
import { useBindLead, useMatchCandidates } from '../useLeadBinding.js';

const CREATE_VALUE = 'create';
const CONTACT_PREFIX = 'contact:';
const contactValue = (id: string) => `${CONTACT_PREFIX}${id}`;

export interface LeadClientMatchDialogProps {
  leadId: string;
  clientId: string;
  onClose: () => void;
  /** Fired after a successful bind so the caller can close its own picker. */
  onBound: () => void;
}

export function LeadClientMatchDialog({
  leadId,
  clientId,
  onClose,
  onBound,
}: LeadClientMatchDialogProps) {
  const { t } = useTranslation(['leads', 'common']);
  const candidates = useMatchCandidates(leadId, clientId);
  const bind = useBindLead(leadId);
  const show = useToastStore((s) => s.show);
  const data = candidates.data;

  // Bind-target selection: a contact (`contact:<id>`) or the create-new option.
  // Seeded once the proposal loads: prefer the registry-matched contact, else
  // create a new contact from the lead's suggested name. `touched` flips only on
  // a user edit (never the programmatic seed), so it is the honest dirty signal.
  const [selection, setSelection] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!data) return;
    setSelection(
      (prev) =>
        prev ?? (data.matchedContactId ? contactValue(data.matchedContactId) : CREATE_VALUE),
    );
    setName((prev) => (prev === '' ? data.suggestedContactName : prev));
  }, [data]);

  const options: SelectOption[] = useMemo(() => {
    if (!data) return [];
    const contactOpts = data.contacts.map((c) => ({
      value: contactValue(c.id),
      label: c.matchesIdentifier
        ? t('leads:match.contactMatched', { name: c.fullName })
        : c.fullName,
    }));
    return [...contactOpts, { value: CREATE_VALUE, label: t('leads:match.createOption') }];
  }, [data, t]);

  const effectiveSelection = selection ?? CREATE_VALUE;
  const isCreate = effectiveSelection === CREATE_VALUE;
  const trimmedName = name.trim();
  const canConfirm = !!data && !bind.isPending && (isCreate ? trimmedName.length > 0 : true);

  const changeSelection = (v: string) => {
    setTouched(true);
    setSelection(v);
  };
  const changeName = (v: string) => {
    setTouched(true);
    setName(v);
  };

  const confirm = () => {
    if (!data) return;
    const input: BindLeadInput = isCreate
      ? { mode: 'create', clientId, fullName: trimmedName }
      : { mode: 'matched', clientId, contactId: effectiveSelection.slice(CONTACT_PREFIX.length) };
    bind.mutate(input, {
      onSuccess: () => {
        show({ tone: 'success', text: t('leads:toasts.clientBound') });
        onBound();
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('leads:match.title')}
      width={460}
      dirty={touched}
      busy={bind.isPending}
      onConfirmClose={onClose}
    >
      <MatchDialogBody
        isPending={candidates.isPending}
        isError={candidates.isError}
        hasData={!!data}
        options={options}
        effectiveSelection={effectiveSelection}
        onChangeSelection={changeSelection}
        isCreate={isCreate}
        name={name}
        onChangeName={changeName}
        canConfirm={canConfirm}
        onConfirm={confirm}
        onClose={onClose}
      />
    </EditorModal>
  );
}

// Body + actions live inside the EditorModal subtree so the Cancel affordance
// can route through the guard's `confirmDestructive` (dirty → prompt).
function MatchDialogBody({
  isPending,
  isError,
  hasData,
  options,
  effectiveSelection,
  onChangeSelection,
  isCreate,
  name,
  onChangeName,
  canConfirm,
  onConfirm,
  onClose,
}: {
  isPending: boolean;
  isError: boolean;
  hasData: boolean;
  options: SelectOption[];
  effectiveSelection: string;
  onChangeSelection: (v: string) => void;
  isCreate: boolean;
  name: string;
  onChangeName: (v: string) => void;
  canConfirm: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const { confirmDestructive } = useEditorGuard();
  return (
    <div style={{ display: 'grid', gap: 'var(--s-4)' }}>
      {isPending && <LoadingState text={t('leads:match.loading')} />}
      {isError && <ErrorState title={t('leads:match.loadError')} />}
      {hasData && (
        <div style={{ display: 'grid', gap: 'var(--s-4)' }} data-testid="lead-match-body">
          <FormRow label={t('leads:match.targetLabel')}>
            <Select value={effectiveSelection} onChange={onChangeSelection} options={options} />
          </FormRow>
          {isCreate && (
            <FormRow label={t('leads:match.contactNameLabel')}>
              <Input value={name} onChange={onChangeName} />
            </FormRow>
          )}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          kind="primary"
          disabled={!canConfirm}
          onClick={onConfirm}
          data-testid="lead-match-confirm"
        >
          {t('leads:match.confirm')}
        </Button>
      </div>
    </div>
  );
}
